import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Center, Loader, Stack, Text } from '@mantine/core';
import { useQuery } from '@apollo/client';

import BaseRequestsTable, { RenderBodyCtx } from './BaseRequestsTable';
import { getPricingBatchesColumns } from './columns/pricingBatchesColumns';
import { useAuthStore } from '../../stores/auth.store';
import { GET_PRICE_CHANGE_REQUEST_BATCHES_TABLE_DATA } from '../../apollo/queries/priceChangeRequest';
import { BatchCard } from './batches/BatchCard';
import { BatchExpansionProvider } from './batches/BatchExpansionContext';
import { PricingBatchStagingProvider } from './batches/PricingBatchStagingContext';
import { PricingBatchStagedSubmitButton } from './batches/PricingBatchStagedSubmitButton';
import { PricingBatchesQuickActionExpansionSync } from './batches/PricingBatchesQuickActionExpansionSync';
import { PricingBatchesUnsavedChangesGuard } from './batches/PricingBatchesUnsavedChangesGuard';
import { When } from '../common';
import ProofOfPricingModal from './components/ProofOfPricingModal';
import type { ProofDocumentForModal } from './components/ProofOfPricingModal';
import type { PriceChangeRequestBatch, Request } from '../../codegen/graphql';

function BatchesBody({
    input,
    onTotalChange,
    onRefresh,
    onSelectedIdsChange,
    openRequestDrawer,
}: RenderBodyCtx) {
    const skip = !input.userIds || input.userIds.length === 0;

    const batchesInput = useMemo(
        () => ({
            ...input,
            take: undefined,
            skip: undefined,
            productSku: null,
            storeIds: null,
            productCategory: null,
            friendlyId: null,
        }),
        [input],
    );

    const { data, loading, error, refetch } = useQuery(
        GET_PRICE_CHANGE_REQUEST_BATCHES_TABLE_DATA,
        {
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true,
            pollInterval: 120000,
            skip,
            variables: { input: batchesInput },
        },
    );

    useEffect(() => {
        onRefresh(() => refetch());
    }, [onRefresh, refetch]);

    const total =
        data?.getPriceChangeRequestBatchesTableData?.pagination?.total ?? 0;
    useEffect(() => {
        onTotalChange(total);
    }, [total, onTotalChange]);

    const batches = useMemo(
        () => data?.getPriceChangeRequestBatchesTableData?.batches ?? [],
        [data],
    );

    const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);
    const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
    const [proofViewerDocuments, setProofViewerDocuments] = useState<
        ProofDocumentForModal[] | null
    >(null);

    const handleOpenBatchProofViewer = useCallback(
        (batch: PriceChangeRequestBatch) => {
            const proofs = batch.proofOfCompetitorPricing ?? [];
            if (proofs.length === 0) {
                return;
            }
            setProofViewerDocuments(
                proofs.map(p => ({
                    details: p.details
                        ? {
                              fileName: p.details.fileName,
                              fileType:
                                  p.details.fileType ?? p.details.fileMimeType,
                          }
                        : null,
                    downloadUrl: p.downloadUrl ?? null,
                })),
            );
        },
        [],
    );

    const handleCloseProofViewer = useCallback(() => {
        setProofViewerDocuments(null);
    }, []);

    const openDrawerFromBatchGrouping = useCallback(
        (request: Request) =>
            openRequestDrawer(request, { fromGrouping: true }),
        [openRequestDrawer],
    );

    const batchIdsKey = useMemo(
        () => batches.map(b => b.batchId).join('|'),
        [batches],
    );
    const rowIdsKey = useMemo(
        () => batches.flatMap(b => b.requests.map(r => r.id)).join('|'),
        [batches],
    );
    useEffect(() => {
        const batchIdSet = new Set(batchIdsKey ? batchIdsKey.split('|') : []);
        setSelectedBatchIds(prev => {
            const next = prev.filter(id => batchIdSet.has(id));
            return next.length === prev.length ? prev : next;
        });
        const rowIdSet = new Set(rowIdsKey ? rowIdsKey.split('|') : []);
        setSelectedRowIds(prev => {
            const next = prev.filter(id => rowIdSet.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, [batchIdsKey, rowIdsKey]);

    const effectiveSelectedIds = useMemo(() => {
        const selectedBatchSet = new Set(selectedBatchIds);
        const fromBatches = batches
            .filter(b => selectedBatchSet.has(b.batchId))
            .flatMap(b => b.requests.map(r => r.id));
        return Array.from(new Set([...fromBatches, ...selectedRowIds]));
    }, [batches, selectedBatchIds, selectedRowIds]);

    useEffect(() => {
        onSelectedIdsChange(effectiveSelectedIds);
    }, [effectiveSelectedIds, onSelectedIdsChange]);

    const handleBatchSelectedChange = useCallback(
        (batchId: string, requestIds: string[], checked: boolean) => {
            setSelectedBatchIds(prev =>
                checked
                    ? Array.from(new Set([...prev, batchId]))
                    : prev.filter(id => id !== batchId),
            );
            if (!checked) {
                setSelectedRowIds(prev =>
                    prev.filter(id => !requestIds.includes(id)),
                );
            }
        },
        [],
    );

    const handleBatchRowIdsChange = useCallback(
        (
            batchId: string,
            batchRequestIds: string[],
            nextIdsForBatch: string[],
        ) => {
            const batchSet = new Set(batchRequestIds);
            const nextSet = new Set(nextIdsForBatch);
            const isFullBatch =
                batchRequestIds.length > 0 &&
                nextIdsForBatch.length === batchRequestIds.length &&
                batchRequestIds.every(id => nextSet.has(id));

            setSelectedBatchIds(prev =>
                isFullBatch ? prev : prev.filter(id => id !== batchId),
            );
            setSelectedRowIds(prev => [
                ...prev.filter(id => !batchSet.has(id)),
                ...nextIdsForBatch,
            ]);
        },
        [],
    );

    const isLoadingInitial = loading && batches.length === 0;
    const isEmpty = !isLoadingInitial && !error && batches.length === 0;
    const hasBatches = !isLoadingInitial && !error && batches.length > 0;

    return (
        <>
            <ProofOfPricingModal
                open={proofViewerDocuments !== null}
                layout="floating"
                documents={proofViewerDocuments ?? []}
                initialIndex={0}
                onClose={handleCloseProofViewer}
            />
            <When condition={isLoadingInitial}>
                <Center py="xl">
                    <Loader size="sm" />
                </Center>
            </When>
            <When condition={!!error}>
                <Text ta="center" py="xl" size="sm" c="red">
                    Failed to load batches.
                </Text>
            </When>
            <When condition={isEmpty}>
                <Text ta="center" py="xl" size="sm" c="dimmed">
                    No batches to display.
                </Text>
            </When>
            <When condition={hasBatches}>
                <BatchExpansionProvider>
                    <PricingBatchesQuickActionExpansionSync />
                    <Stack gap="md">
                        {batches.map(batch => {
                            const batchRequestIds = batch.requests.map(
                                r => r.id,
                            );
                            const rowIdsForBatch = effectiveSelectedIds.filter(
                                id => batchRequestIds.includes(id),
                            );
                            return (
                                <BatchCard
                                    key={batch.batchId}
                                    batch={batch}
                                    baseFilter={batchesInput}
                                    openRequestDrawer={
                                        openDrawerFromBatchGrouping
                                    }
                                    selected={selectedBatchIds.includes(
                                        batch.batchId,
                                    )}
                                    onSelectedChange={checked =>
                                        handleBatchSelectedChange(
                                            batch.batchId,
                                            batchRequestIds,
                                            checked,
                                        )
                                    }
                                    selectedRowIds={rowIdsForBatch}
                                    onSelectedRowIdsChange={nextIds =>
                                        handleBatchRowIdsChange(
                                            batch.batchId,
                                            batchRequestIds,
                                            nextIds,
                                        )
                                    }
                                    onRowClick={openDrawerFromBatchGrouping}
                                    onOpenBatchProofViewer={
                                        handleOpenBatchProofViewer
                                    }
                                    defaultOpen
                                    enableInnerTableFilters={false}
                                    paginateInnerTable={false}
                                />
                            );
                        })}
                    </Stack>
                </BatchExpansionProvider>
            </When>
        </>
    );
}

function PricingBatchesTable() {
    const { userId, standInForUserIds } = useAuthStore();

    const userIds = useMemo(() => {
        if (!userId) {
            return null;
        }
        return [userId, ...standInForUserIds];
    }, [userId, standInForUserIds]);

    return (
        <PricingBatchStagingProvider>
            <PricingBatchesUnsavedChangesGuard>
                <BaseRequestsTable
                    userIds={userIds}
                    onlyPendingResponsibleActions
                    showDateRangeSelector={false}
                    excludeCreatedDateFromFilter
                    tableHeightOffset="15rem"
                    buildColumns={getPricingBatchesColumns}
                    renderBody={ctx => <BatchesBody {...ctx} />}
                    detachedBody
                    toolbarTrailing={<PricingBatchStagedSubmitButton />}
                />
            </PricingBatchesUnsavedChangesGuard>
        </PricingBatchStagingProvider>
    );
}

export default PricingBatchesTable;
